import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FunctionComponent, SVGProps } from 'react';
import { Modal } from 'antd';
import * as XLSX from 'xlsx';
import type { HierarchyView, NodeKind } from './DefineBoundaryStep';
import {
  useLazyGetHierarchyTemplateQuery,
  useLazyGetHierarchyExportQuery,
  useUploadHierarchyMutation,
  useGetNodeTreeQuery,
  useCreateNodeAreaMutation,
  useDeleteNodeAreaMutation,
  useUpdateNodeAreaMutation,
  type OrgNode,
  type OrgNodeArea,
} from '../../../features/organizations/organizationsApi';
import {
  territoryApi,
  useGetEcLevelsQuery,
  type AreaInfoDto,
} from '../../../features/territory/territoryApi';
import { kindFromLevelName } from '../../../features/territory/constituencyTree';
import { useAppDispatch } from '../../../app/hooks';
import type { AppDispatch } from '../../../app/store';
import { Spinner } from '../../../components/ui';
import {
  SearchIcon,
  CaretIcon,
  CheckmarkIcon,
  ChevronRightIcon,
  FilterIcon,
  ColumnsIcon,
  KebabIcon,
  ArrowRightIcon,
  LinkIcon,
  TrashIcon,
  TemplateDocIcon,
  BulkUploadIcon,
  HierarchyNodesIcon,
  NodeBoothIcon,
} from '../../../assets/icons';

type IconType = FunctionComponent<SVGProps<SVGSVGElement>>;

/** The org's internal hierarchy tree from /organisation/node/tree/ (left panel). */
function InternalOrgTree({
  nodes,
  onBoothClick,
  activeCode,
  ecLevelById,
  leafLocate,
}: {
  nodes: OrgNode[];
  /** Clicking an area asks the EC panel to locate it; `levelHint` is the area's EC level. */
  onBoothClick?: (areaCode: string, boothName: string, levelHint?: string) => void;
  /** area_code currently being located (highlighted). */
  activeCode?: string | null;
  /** EcLocationLevel id → level_name, so a mapped area's real EC level drives area-info. */
  ecLevelById?: Record<number, string>;
  /** Admin flow: a leaf level maps 1:1 to an EC area — click the node itself to locate it
   * (one click, like a booth in step 4). The level still comes from its ec_location_level_id. */
  leafLocate?: boolean;
}) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const renderNode = (node: OrgNode, id: string) => {
    const kids = node.children ?? [];
    const areas = node.areas ?? [];
    const hasChildren = kids.length > 0 || areas.length > 0;
    const open = !collapsed.has(id);
    // The area's real EC level (from where it was mapped, via ec_location_level_id) is the
    // level to locate by — for both the org (step 4) and admin (step 6) flows. The admin
    // node's own level_name (e.g. "Dt" / "Gram Panchayat") is NOT an EC level, so when the
    // mapped level is missing we infer the real EC level from the area code instead (a numeric
    // code like "72389" is a Village, not the parent GP node it happens to sit under).
    const areaLevelHint = (a: OrgNodeArea): string | undefined =>
      (a.ec_location_level_id != null ? ecLevelById?.[a.ec_location_level_id] : undefined) ??
      levelFromAreaCode(a.area_code);
    // The kind of area mapped here (booth / village / gram panchayat / …).
    const areaNoun = (
      (areas[0] &&
        (normalizeLevel(areaLevelHint(areas[0])) ?? levelFromAreaCode(areas[0].area_code))) ||
      'Booth'
    ).toLowerCase();
    // Admin flow: a leaf level with a SINGLE area — click the node itself to locate it
    // (like clicking a booth in step 4). Nodes with multiple areas expand to list them all.
    const leafArea = leafLocate && kids.length === 0 && areas.length === 1 ? areas[0] : undefined;
    const leafActive = leafArea != null && activeCode === leafArea.area_code;
    return (
      <div key={id}>
        <button
          type="button"
          onClick={() => {
            if (leafArea) {
              onBoothClick?.(
                leafArea.area_code,
                node.org_name || leafArea.booth_name || leafArea.area_code,
                areaLevelHint(leafArea),
              );
            } else if (hasChildren) {
              toggle(id);
            }
          }}
          className={[
            'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs font-semibold tracking-[0.24px] text-ink transition-colors',
            leafActive ? 'bg-brand-shade' : 'hover:bg-brand-shade/60',
          ].join(' ')}
        >
          {leafArea ? (
            <NodeBoothIcon className="size-4 shrink-0 text-brand" />
          ) : hasChildren ? (
            <CaretIcon
              className={['size-3 shrink-0 text-ink-secondary', open ? '' : '-rotate-90'].join(' ')}
            />
          ) : (
            <span className="size-3 shrink-0" />
          )}
          <span className="shrink-0 rounded bg-[#e5eeff] px-1.5 py-0.5 text-[10px] font-medium text-brand">
            {node.level_name}
          </span>
          <span className="truncate">{node.org_name}</span>
          {/* Sub-unit count: "N Districts" when the child level name is set (step 6's admin
              tree), else just the number (step 4's party tree carries no level names). Hidden
              for a single child (e.g. the lone State root). */}
          {kids.length > 1 && !leafArea && (
            <span className="ml-auto shrink-0 rounded bg-[#e5eeff] px-1.5 py-0.5 text-[10px] font-medium text-brand">
              {kids[0]?.level_name ? `${kids.length} ${kids[0].level_name}s` : kids.length}
            </span>
          )}
          {/* Mapped-area count — only on leaf nodes (no children); an intermediate node like a
              District shows its sub-unit count, not a stray "1 state" from its own EC mapping. */}
          {areas.length > 0 && kids.length === 0 && !leafArea && (
            <span className="ml-auto shrink-0 rounded bg-[#dcfce7] px-1.5 py-0.5 text-[10px] font-medium text-[#166534]">
              {areas.length} {areaNoun}
              {areas.length > 1 ? 's' : ''}
            </span>
          )}
          {leafArea && (
            <span className="ml-auto shrink-0 text-[10px] text-ink-secondary">{leafArea.area_code}</span>
          )}
        </button>
        {open && hasChildren && !leafArea && (
          <div className="ml-[13px] flex flex-col gap-1 border-l border-[rgba(195,198,215,0.5)] pl-3">
            {kids.map((c, i) => renderNode(c, `${id}.${i}`))}
            {areas.map((a, i) => {
              const active = activeCode != null && a.area_code === activeCode;
              return (
                <button
                  key={`${id}-a-${i}`}
                  type="button"
                  onClick={() =>
                    onBoothClick?.(a.area_code, a.booth_name || a.area_code, areaLevelHint(a))
                  }
                  className={[
                    'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors',
                    active ? 'bg-brand-shade' : 'hover:bg-brand-shade/60',
                  ].join(' ')}
                >
                  <NodeBoothIcon className="size-4 shrink-0 text-brand" />
                  <span className="truncate font-medium text-ink">
                    {a.booth_name || a.area_code}
                  </span>
                  <span className="ml-auto shrink-0 text-[10px] text-ink-secondary">
                    {a.area_code}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return <div className="flex flex-col gap-1">{nodes.map((n, i) => renderNode(n, `n${i}`))}</div>;
}

/* ------------------------------------------------------------------ *
 * Destination miller-columns (Election Commission Structure)
 * ------------------------------------------------------------------ */

interface DestNode {
  id: string;
  label: string;
  /** Kind + code + context — used to lazily fetch this node's children on drill. */
  kind?: NodeKind;
  code?: string;
  acCode?: string;
  branch?: 'urban' | 'rural';
  /** Pre-loaded children (from the step-2 selection path); deeper levels load lazily. */
  children?: DestNode[];
  /** Was this node checked in step 2 — the level to auto-open in step 4. */
  selected?: boolean;
  /** Booth nodes can be mapped to a source unit. */
  linkable?: boolean;
  /** Booth area/ps code — matched against the Internal Organization tree to auto-detect links. */
  areaCode?: string;
  /** Node belongs to a located-area "location" view (built from area-info) — a fixed
   * breadcrumb that must not lazily fetch children on click. */
  located?: boolean;
}

/** A booth already present in the Internal Organization tree, keyed by area_code. */
interface LinkedArea {
  /** node-area record id — the target of the de-link (DELETE) / re-link (PATCH) calls. */
  nodeAreaId?: number;
  boothName: string;
  orgName: string;
}

/**
 * Index every booth area_code in the Internal Organization tree (from the upload)
 * so an EC booth sharing that code can be shown as already linked (and de-/re-linked).
 */
function collectInternalAreas(nodes: OrgNode[]): Map<string, LinkedArea> {
  const map = new Map<string, LinkedArea>();
  const walk = (n: OrgNode) => {
    (n.areas ?? []).forEach((a) => {
      if (a.area_code) {
        map.set(a.area_code, {
          nodeAreaId: a.id,
          boothName: a.booth_name || a.area_code,
          orgName: n.org_name,
        });
      }
    });
    (n.children ?? []).forEach(walk);
  };
  nodes.forEach(walk);
  return map;
}

const run = <T,>(p: { unwrap: () => Promise<T> }) => p.unwrap();

/** "State › PC › AC › … › Booth" path from a booth-info record. */
type AreaLevel =
  | 'Booth'
  | 'Village'
  | 'Ward'
  | 'GramPanchayat'
  | 'Assembly'
  | 'Parliament'
  | 'State';

/** Map a level name (e.g. an org node's level_name) to an area-info level, if it is one. */
function normalizeLevel(s?: string | null): AreaLevel | undefined {
  const t = (s || '').toLowerCase().replace(/[\s_-]+/g, '');
  if (!t) return undefined;
  if (t.includes('booth')) return 'Booth';
  if (t.includes('ward')) return 'Ward';
  if (t.includes('village')) return 'Village';
  if (t.includes('grampanchayat') || t.includes('panchayat') || t === 'gp') return 'GramPanchayat';
  if (t.includes('assembly')) return 'Assembly';
  if (t.includes('parliament') || t === 'mp') return 'Parliament';
  if (t.includes('state')) return 'State';
  return undefined;
}

/**
 * Infer the EC level of a mapped area from its code, so we can query area-info.
 * Booth ps-codes look like `S20_08_065_001` (4 groups); an assembly ac-code is the
 * first 3 (`S20_08_065`). Plain numeric codes (census/lgd) are village-level.
 */
function levelFromAreaCode(code: string): AreaLevel {
  const c = (code || '').trim();
  if (/^S\d/i.test(c)) {
    const segs = c.split('_');
    if (segs.length >= 4) return 'Booth';
    if (segs.length === 3) return 'Assembly';
    if (segs.length === 2) return 'Parliament';
    if (segs.length === 1) return 'State';
  }
  return 'Village';
}

/** The clicked area's location breadcrumb: State › … › <clicked entity>. */
function areaLocationPath(info: AreaInfoDto): string {
  const parts = [
    info.state?.name,
    info.parliament?.name,
    info.assembly?.name,
    info.gram_panchayat?.name,
    info.ward?.name,
    info.village?.name,
    info.booth?.name,
  ].filter(Boolean) as string[];
  // Append the clicked entity (its own level) if it isn't already the last ancestor.
  const leaf = info.booth?.name || info.name || '';
  if (leaf && parts[parts.length - 1] !== leaf) parts.push(leaf);
  return parts.join(' › ');
}

/**
 * Build the EC structure (top-down DestNode tree) for a located area from area-info:
 * State → Parliament → Assembly → [Gram Panchayat → Ward → Village] → [Booth]. Levels
 * that are null in the response are skipped, and the clicked entity is the leaf.
 */
function areaInfoToTree(info: AreaInfoDto, clickedCode?: string): DestNode[] {
  // `clickedCode` is the area_code we located from the Internal Organization tree — the
  // key `linkedAreas` uses. The leaf must carry it (not the API's echoed `info.code`,
  // which can differ) so relink/unlink resolve instead of falling back to "Link".
  const leafCode = info.booth?.ps_code || clickedCode || info.code || '';
  const base = `loc-${leafCode || info.level || 'area'}`;
  type Level = {
    key: string;
    name?: string | null;
    code?: string | null;
    linkable?: boolean;
    kind?: NodeKind;
    /** Extra label suffix (e.g. "3,545 electors") appended after name · code. */
    note?: string;
  };
  // Electors from area-info (village-level) — shown on the located leaf row.
  const electorsNote =
    info.total_electors != null ? `${info.total_electors.toLocaleString('en-IN')} electors` : undefined;
  const gpCode = info.gram_panchayat?.lgd_code ?? info.gram_panchayat?.gram_panchayat_lgd_code;
  const levels: Level[] = (
    [
      { key: 'state', name: info.state?.name, kind: 'state' },
      { key: 'pc', name: info.parliament?.name, code: info.parliament?.pc_code, kind: 'parliament' },
      { key: 'ac', name: info.assembly?.name, code: info.assembly?.ac_code, kind: 'assembly' },
      { key: 'gp', name: info.gram_panchayat?.name, code: gpCode, kind: 'gram' },
      { key: 'ward', name: info.ward?.name, code: info.ward?.ward_code, kind: 'ward' },
      { key: 'village', name: info.village?.name, kind: 'village' },
      { key: 'booth', name: info.booth?.name, code: info.booth?.ps_code, linkable: true, kind: 'booth' },
    ] as Level[]
  ).filter((l) => l.name);
  // Ensure the clicked entity is the leaf and is actionable. The clicked leaf
  // (booth, village, ward, …) exists in the Internal Organization tree, so it can
  // always be relinked or unlinked. Append it as its own level when the ancestry
  // doesn't already end on it — this also splits a same-named ancestor (e.g. a
  // village "Baghor" sitting under its GP "Baghor") into distinct rows by comparing
  // the located entity's EC kind, not just its name.
  const leafName = info.booth?.name || info.name;
  const leafKind = kindFromLevelName(info.level) ?? undefined;
  const last = levels[levels.length - 1];
  if (leafName && (!last || last.name !== leafName || (leafKind && leafKind !== last.kind))) {
    levels.push({
      key: 'leaf',
      name: leafName,
      code: clickedCode || info.code,
      linkable: true,
      kind: leafKind,
      note: electorsNote,
    });
  } else if (last) {
    last.linkable = true;
    last.code = last.code || clickedCode || info.code;
    last.note = last.note ?? electorsNote;
  }

  // Nest bottom-up so the state ends up at the root.
  let child: DestNode | undefined;
  for (let i = levels.length - 1; i >= 0; i -= 1) {
    const l = levels[i];
    let label = l.code ? `${l.name} · ${l.code}` : String(l.name);
    if (l.note) label += ` · ${l.note}`;
    child = {
      id: `${base}/${l.key}`,
      label,
      located: true,
      ...(l.kind ? { kind: l.kind } : {}),
      ...(l.linkable ? { linkable: true, areaCode: l.code || leafCode || undefined } : {}),
      ...(child ? { children: [child] } : {}),
    };
  }
  return child ? [child] : [];
}

/**
 * Convert the step-2 selection path into base DestNodes, stopping at the chosen
 * (selected) node. Ancestors keep their known children (State → PC → AC); below the
 * selected node, children are loaded lazily on drill. `acCode` is carried down so
 * rural/urban branches can query the right assembly.
 */
function toBaseTree(views: HierarchyView[], acCode?: string): DestNode[] {
  return views.map((v) => {
    const nextAc = v.kind === 'assembly' ? v.code : acCode;
    const branch =
      v.kind === 'branch' ? (v.label.toLowerCase().includes('urban') ? 'urban' : 'rural') : undefined;
    const areaCode = v.areaCode || v.code;
    const node: DestNode = {
      id: v.id,
      label: v.label,
      kind: v.kind,
      code: v.code,
      acCode: nextAc,
      branch,
      areaCode: v.areaCode,
      selected: v.selected,
      ...linkFields(v.kind, areaCode),
    };
    if (!v.selected && (v.children?.length ?? 0) > 0) {
      node.children = toBaseTree(v.children, nextAc);
    }
    return node;
  });
}

/** The node checked in step 2 (deepest selected) — its children are auto-loaded in step 4. */
function findSelectedNode(nodes: DestNode[]): DestNode | undefined {
  for (const n of nodes) {
    if (n.selected) return n;
    const found = n.children ? findSelectedNode(n.children) : undefined;
    if (found) return found;
  }
  return undefined;
}

/** Fetch one node's direct children from the territory API — a single call per drill. */
async function fetchDestChildren(dispatch: AppDispatch, node: DestNode): Promise<DestNode[]> {
  const ep = territoryApi.endpoints;
  const cid = (suffix: string) => `${node.id}/${suffix}`;
  switch (node.kind) {
    case 'state': {
      const pcs = await run(dispatch(ep.getParliaments.initiate({ state: node.code! }, { subscribe: false })));
      return pcs.map((p) => ({ id: cid(`pc-${p.id}`), label: p.name || `PC ${p.id}`, kind: 'parliament', code: String(p.id) }));
    }
    case 'parliament': {
      const acs = await run(dispatch(ep.getAssemblies.initiate({ pc: node.code! }, { subscribe: false })));
      return acs.map((a) => ({
        id: cid(`ac-${a.id}`),
        label: a.name || `AC ${a.id}`,
        kind: 'assembly',
        code: a.ac_code || '',
        acCode: a.ac_code || '',
        ...linkFields('assembly', a.ac_code || ''),
      }));
    }
    case 'assembly':
      return [
        { id: cid('rural'), label: 'Rural', kind: 'branch', branch: 'rural', code: '', acCode: node.code },
        { id: cid('urban'), label: 'Urban', kind: 'branch', branch: 'urban', code: '', acCode: node.code },
      ];
    case 'branch': {
      if (node.branch === 'urban') {
        const ulbs = await run(dispatch(ep.getUlbs.initiate(undefined, { subscribe: false })));
        return ulbs.map((u) => ({
          id: cid(`ulb-${u.block_ulb_code ?? u.block_pk}`),
          label: u.name_en || `ULB ${u.block_pk}`,
          kind: 'ulb',
          code: u.block_ulb_code || '',
          ...linkFields('ulb', u.block_ulb_code || ''),
        }));
      }
      const gps = await run(dispatch(ep.getGramPanchayats.initiate({ acCode: node.acCode || '' }, { subscribe: false })));
      return gps.map((g) => ({
        id: cid(`gp-${g.gram_panchayat_lgd_code ?? g.gram_panchayat_pk}`),
        label: g.name_en || `GP ${g.gram_panchayat_pk}`,
        kind: 'gram',
        code: g.gram_panchayat_lgd_code || '',
        ...linkFields('gram', g.gram_panchayat_lgd_code || ''),
      }));
    }
    case 'ulb': {
      const wards = await run(dispatch(ep.getWards.initiate({ ulbCode: node.code! }, { subscribe: false })));
      return wards.map((w) => ({
        id: cid(`ward-${w.ward_code ?? w.ward_pk}`),
        label: w.ward_name_en || `Ward ${w.ward_pk}`,
        kind: 'ward',
        code: w.ward_code || '',
        ...linkFields('ward', w.ward_code || ''),
      }));
    }
    case 'gram': {
      const vs = await run(dispatch(ep.getRuralVillages.initiate({ gpCode: node.code! }, { subscribe: false })));
      return vs.map((v) => ({
        id: cid(`vil-${v.village_result_code ?? v.village_pk}`),
        label: v.name_en || `Village ${v.village_pk}`,
        kind: 'village',
        code: v.village_result_code || '',
        ...linkFields('village', v.village_result_code || ''),
      }));
    }
    case 'ward': {
      const vs = await run(dispatch(ep.getUrbanVillages.initiate({ wardCode: node.code! }, { subscribe: false })));
      return vs.map((v) => ({
        id: cid(`vil-${v.village_result_code ?? v.village_pk}`),
        label: v.name_en || `Village ${v.village_pk}`,
        kind: 'village',
        code: v.village_result_code || '',
        ...linkFields('village', v.village_result_code || ''),
      }));
    }
    case 'village': {
      const booths = await run(dispatch(ep.getBooths.initiate({ villageResultCode: node.code! }, { subscribe: false })));
      return booths.map((b) => ({
        id: cid(`booth-${b.ps_code}`),
        label: b.booth_name ? `${b.booth_name} · ${b.ps_code}` : `Booth ${b.ps_code}`,
        kind: 'booth',
        linkable: true,
        areaCode: b.ps_code,
      }));
    }
    default:
      return [];
  }
}

/** Every expandable node id in the org tree (has children or booth areas). */
function expandableNodeIds(nodes: OrgNode[]): string[] {
  const ids: string[] = [];
  const walk = (n: OrgNode, id: string) => {
    const kids = n.children ?? [];
    if (kids.length > 0 || (n.areas ?? []).length > 0) ids.push(id);
    kids.forEach((c, i) => walk(c, `${id}.${i}`));
  };
  nodes.forEach((n, i) => walk(n, `n${i}`));
  return ids;
}

/**
 * The real Internal Organization structure in the re-link popup — drill and pick the
 * org node (SK) to move a booth to. Booth areas are shown for context only; the
 * selection returns the chosen node's `organisation_node` id.
 */
function SourceMapTree({
  nodes,
  selectedNodeId,
  onSelect,
}: {
  nodes: OrgNode[];
  selectedNodeId: number | null;
  onSelect: (nodeId: number, label: string) => void;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(expandableNodeIds(nodes)));
  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const renderNode = (node: OrgNode, id: string, depth: number) => {
    const kids = node.children ?? [];
    const areas = node.areas ?? [];
    const hasChildren = kids.length > 0 || areas.length > 0;
    const open = expanded.has(id);
    const selectable = node.id != null;
    const selected = selectable && selectedNodeId === node.id;
    return (
      <div key={id}>
        <div
          className={[
            'flex items-center gap-2 rounded-md py-1.5 pr-2 text-xs transition-colors',
            selected ? 'bg-[rgba(0,74,198,0.1)]' : 'hover:bg-brand-shade/60',
          ].join(' ')}
          style={{ paddingLeft: depth * 16 + 8 }}
        >
          {hasChildren ? (
            <button
              type="button"
              onClick={() => toggle(id)}
              className="flex size-4 shrink-0 items-center justify-center text-ink-secondary"
              aria-label={open ? 'Collapse' : 'Expand'}
            >
              <CaretIcon className={['size-3', open ? '' : '-rotate-90'].join(' ')} />
            </button>
          ) : (
            <span className="size-4 shrink-0" />
          )}
          <button
            type="button"
            disabled={!selectable}
            onClick={() => selectable && onSelect(node.id!, node.org_name)}
            className="flex min-w-0 flex-1 items-center gap-2 text-left disabled:cursor-not-allowed"
          >
            <span className="shrink-0 rounded bg-[#e5eeff] px-1.5 py-0.5 text-[10px] font-medium text-brand">
              {node.level_name}
            </span>
            <span
              className={['truncate', selected ? 'font-semibold text-brand' : 'text-ink'].join(' ')}
            >
              {node.org_name}
            </span>
          </button>
          {selected && <CheckmarkIcon className="size-4 shrink-0 text-brand" />}
        </div>
        {open && hasChildren && (
          <>
            {kids.map((c, i) => renderNode(c, `${id}.${i}`, depth + 1))}
            {areas.map((a, i) => (
              <div
                key={`${id}.a${i}`}
                className="flex items-center gap-2 rounded-md py-1.5 pr-2 text-xs text-ink-secondary"
                style={{ paddingLeft: (depth + 1) * 16 + 8 }}
              >
                <span className="size-4 shrink-0" />
                <NodeBoothIcon className="size-4 shrink-0 text-ink-secondary/60" />
                <span className="truncate">{a.booth_name || a.area_code}</span>
              </div>
            ))}
          </>
        )}
      </div>
    );
  };

  if (nodes.length === 0) {
    return (
      <p className="px-2 py-6 text-center text-sm text-ink-secondary">
        Upload the hierarchy Excel first to populate the Internal Organization structure.
      </p>
    );
  }

  return (
    <div className="max-h-[360px] overflow-auto">
      {nodes.map((n, i) => renderNode(n, `n${i}`, 0))}
    </div>
  );
}

/** Row-level booth link actions (linked badge + relink/unlink, or a Link button). */
function BoothActions({
  node,
  link,
  busy,
  onUnlink,
  onOpenRelink,
  onOpenLink,
}: {
  node: DestNode;
  link: LinkedArea | undefined;
  busy: boolean;
  onUnlink: (nodeAreaId: number) => void;
  onOpenRelink: (nodeAreaId: number, label: string) => void;
  onOpenLink: (areaCode: string, boothName: string, kind?: NodeKind) => void;
}) {
  const nodeAreaId = link?.nodeAreaId;
  if (link) {
    return (
      <span className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
        <span
          className="flex max-w-[140px] items-center gap-1 rounded bg-[#dcfce7] px-1.5 py-0.5 text-[10px] font-medium text-[#166534]"
          title={`Linked to ${link.orgName} · ${link.boothName}`}
        >
          <LinkIcon className="size-3 shrink-0" />
          <span className="truncate">{link.orgName}</span>
        </span>
        {nodeAreaId != null && (
          <>
            <button
              type="button"
              disabled={busy}
              onClick={() => onOpenRelink(nodeAreaId, node.label)}
              className="rounded px-1 py-1 text-[11px] font-semibold text-brand transition-colors hover:bg-brand-shade disabled:opacity-50"
              title="Move this unit to another org node"
            >
              Relink
            </button>
            <button
              type="button"
              aria-label="Unlink"
              disabled={busy}
              onClick={() => onUnlink(nodeAreaId)}
              className="flex size-6 items-center justify-center rounded text-[#94a3b8] transition-colors hover:bg-red-50 hover:text-red-500 disabled:opacity-50"
            >
              <TrashIcon className="size-3.5" />
            </button>
          </>
        )}
      </span>
    );
  }
  if (node.areaCode) {
    return (
      <button
        type="button"
        disabled={busy}
        onClick={(e) => {
          e.stopPropagation();
          onOpenLink(node.areaCode!, node.label, node.kind);
        }}
        className="flex items-center gap-1 rounded px-1.5 py-1 text-[11px] font-semibold text-brand transition-colors hover:bg-brand-shade disabled:opacity-50"
      >
        <LinkIcon className="size-3" />
        Link
      </button>
    );
  }
  return null;
}

type GetChildren = (node: DestNode) => DestNode[] | undefined;

/** The id chain of a node and its already-loaded single-child descendants. */
function branchFrom(node: DestNode | undefined, getChildren: GetChildren): string[] {
  const ids: string[] = [];
  let n = node;
  while (n) {
    ids.push(n.id);
    n = getChildren(n)?.[0];
  }
  return ids;
}

/**
 * The path (ids) from a root down to the node checked in step 2 — so step 4 auto-opens
 * exactly the level the user selected (e.g. Urban), not just the first branch.
 */
function firstBranchPath(tree: DestNode[], getChildren: GetChildren): string[] {
  const dfs = (node: DestNode): string[] | null => {
    if (node.selected) return [node.id];
    for (const k of getChildren(node) ?? []) {
      const sub = dfs(k);
      if (sub) return [node.id, ...sub];
    }
    return null;
  };
  for (const root of tree) {
    const p = dfs(root);
    if (p) return p;
  }
  // Fallback (nothing selected): open the first loaded chain.
  return tree[0] ? branchFrom(tree[0], getChildren) : [];
}

/** Human title for each level, shown as the miller-column header. */
const KIND_TITLE: Partial<Record<NodeKind, string>> = {
  state: 'State',
  parliament: 'Parliament Constituency',
  assembly: 'Assembly Constituency',
  branch: 'Settlement',
  ulb: 'Urban Local Body',
  ward: 'Ward',
  gram: 'Gram Panchayat',
  village: 'Village',
  booth: 'Booth',
};

/**
 * EC levels an org node can be mapped to. Any of these can be linked/unlinked in the
 * Election Commission Structure (not just booths) — each carries a real area code.
 */
const LINKABLE_KINDS: ReadonlySet<NodeKind> = new Set<NodeKind>([
  'assembly',
  'ulb',
  'ward',
  'gram',
  'village',
  'booth',
]);

/** node kind → EC location `level_name` (normalized), to resolve its ec_location_level_id on link. */
const KIND_TO_EC_LEVEL: Partial<Record<NodeKind, string>> = {
  state: 'state',
  parliament: 'mp',
  assembly: 'assembly',
  ulb: 'block',
  ward: 'ward',
  gram: 'grampanchayat',
  village: 'village',
  booth: 'booth',
};

/** Linkable-node fields (marks a browse-tree node as mappable at its own level). */
function linkFields(kind: NodeKind | undefined, code?: string | null) {
  return kind && code && LINKABLE_KINDS.has(kind)
    ? { linkable: true, areaCode: code }
    : {};
}

/**
 * Miller-columns view of the Election Commission Structure (matches the Figma): a
 * breadcrumb of the drilled path over horizontal level columns (State → … → Booth).
 * Booth rows carry the link / relink / unlink actions.
 */
function DestinationColumns({
  tree,
  getChildren,
  onDrill,
  loadingNodes,
  linkedAreas,
  sourceTree,
  treeSearch,
  onTreeSearch,
  onLink,
  onUnlink,
  onRelink,
  busyAreaId,
  busyCode,
}: {
  tree: DestNode[];
  /** A node's already-loaded children (undefined = not loaded yet). */
  getChildren: GetChildren;
  /** Load a node's children lazily when it's drilled into. */
  onDrill: (node: DestNode) => void;
  /** Node ids currently fetching their children (shows a loading column). */
  loadingNodes: Set<string>;
  /** Booths already present in the Internal Organization tree, keyed by area_code. */
  linkedAreas: Map<string, LinkedArea>;
  /** The Internal Organization tree — the org nodes a booth can be (re)linked to. */
  sourceTree: OrgNode[];
  /** Search text for the link-modal tree (server-side). */
  treeSearch: string;
  onTreeSearch: (v: string) => void;
  /** Link an unlinked area (any level) to an org node (POST a new node-area record). */
  onLink: (
    areaCode: string,
    boothName: string,
    organisationNodeId: number,
    kind?: NodeKind,
  ) => void;
  /** De-link a booth (DELETE its node-area record). */
  onUnlink: (nodeAreaId: number) => void;
  /** Re-link a booth: move its node-area to another org node (PATCH). */
  onRelink: (nodeAreaId: number, organisationNodeId: number) => void;
  /** node-area id currently being de-/re-linked (disables its actions). */
  busyAreaId: number | null;
  /** area_code of an unlinked booth currently being linked (disables its action). */
  busyCode: string | null;
}) {
  // Open the path down to the selected level (loaded from step 2) on mount.
  const [path, setPath] = useState<string[]>(() => firstBranchPath(tree, getChildren));
  const [linkTarget, setLinkTarget] = useState<
    | { mode: 'relink'; nodeAreaId: number; label: string }
    | { mode: 'link'; areaCode: string; boothName: string; label: string; kind?: NodeKind }
    | null
  >(null);
  const [mapSel, setMapSel] = useState<{ id: number; label: string } | null>(null);

  const openRelink = (nodeAreaId: number, label: string) => {
    setLinkTarget({ mode: 'relink', nodeAreaId, label });
    setMapSel(null);
  };
  const openLink = (areaCode: string, boothName: string, kind?: NodeKind) => {
    setLinkTarget({ mode: 'link', areaCode, boothName, label: boothName, kind });
    setMapSel(null);
  };
  const closeLink = () => {
    setLinkTarget(null);
    setMapSel(null);
  };

  // Re-open the selected path when the selection (tree) changes.
  useEffect(() => {
    setPath(firstBranchPath(tree, getChildren));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree]);

  // Clicking a row selects it in its column and lazily loads its children.
  const select = (colIndex: number, node: DestNode) => {
    setPath((prev) => [...prev.slice(0, colIndex), node.id]);
    onDrill(node);
  };

  // Build the level columns + breadcrumb from the drilled path (lazy children).
  const columns: DestNode[][] = [tree];
  const crumbs: string[] = [];
  let cursor: DestNode[] = tree;
  let loadingCol = false;
  for (let i = 0; i < path.length; i += 1) {
    const node = cursor.find((n) => n.id === path[i]);
    if (!node) break;
    crumbs.push(node.label);
    const kids = getChildren(node);
    if (kids && kids.length > 0) {
      columns.push(kids);
      cursor = kids;
    } else {
      if (node.kind !== 'booth' && loadingNodes.has(node.id)) loadingCol = true;
      break;
    }
  }

  return (
    <>
      {/* Breadcrumb of the drilled path */}
      <div className="flex shrink-0 items-center gap-2 overflow-auto border-b border-[#c3c6d7] bg-[#eff4ff] px-4 pb-[13px] pt-3">
        {crumbs.map((label, i) => {
          const last = i === crumbs.length - 1;
          return (
            <span key={`${label}-${i}`} className="flex shrink-0 items-center gap-2">
              <span
                className={[
                  'text-[11px] font-medium tracking-[0.55px]',
                  last ? 'text-ink' : 'text-brand',
                ].join(' ')}
              >
                {label}
              </span>
              {!last && (
                <ChevronRightIcon className="h-[7px] w-[4.3px] shrink-0 text-ink-secondary" />
              )}
            </span>
          );
        })}
      </div>

      {/* Level columns — reversed view: deepest level (Booth) first, State last. */}
      <div className="flex-1 overflow-x-auto bg-[#f8f9ff]">
        <div className="flex h-full min-w-full">
          {loadingCol && (
            <div className="flex w-[280px] shrink-0 items-center justify-center border-r border-[#c3c6d7]">
              <Spinner />
            </div>
          )}
          {columns
            .map((items, colIndex) => ({ items, colIndex }))
            .reverse()
            .map(({ items, colIndex }) => (
              <div
                key={colIndex}
                className="flex w-[280px] shrink-0 flex-col border-r border-[#c3c6d7] last:border-r-0"
              >
              <div className="shrink-0 border-b border-[#c3c6d7] bg-[rgba(229,238,255,0.3)] px-4 pb-[13px] pt-3 text-[12px] font-semibold uppercase tracking-[0.6px] text-ink">
                {(items[0]?.kind && KIND_TITLE[items[0].kind]) || `Level ${colIndex + 1}`}
              </div>
              <div className="flex flex-1 flex-col gap-1 overflow-auto p-2">
                {items.map((node) => {
                  const canDrill = node.kind !== 'booth';
                  const isLinkable = Boolean(node.linkable);
                  const active = path[colIndex] === node.id;
                  const link = node.areaCode ? linkedAreas.get(node.areaCode) : undefined;
                  const nodeAreaId = link?.nodeAreaId;
                  const busy =
                    (nodeAreaId != null && busyAreaId === nodeAreaId) ||
                    (node.areaCode != null && busyCode === node.areaCode);
                  return (
                    <div
                      key={node.id}
                      onClick={() => select(colIndex, node)}
                      className={[
                        'flex cursor-pointer items-center gap-2 rounded-lg p-[9px] text-left transition-colors',
                        active
                          ? 'border border-[rgba(0,74,198,0.2)] bg-[rgba(0,74,198,0.1)]'
                          : 'border border-transparent hover:bg-brand-shade/50',
                      ].join(' ')}
                    >
                      {/* Inverted view: drill arrow first (points left toward the deeper levels). */}
                      {canDrill && (
                        <ChevronRightIcon className="h-[10px] w-[6px] shrink-0 rotate-180 text-ink-secondary" />
                      )}
                      <span className="min-w-0 flex-1 truncate text-right text-[13px]">
                        <span
                          className={
                            active ? 'font-semibold text-[#00174b]' : 'font-normal text-ink'
                          }
                        >
                          {node.label}
                        </span>
                      </span>
                      {isLinkable && (
                        <span className="flex shrink-0 items-center gap-1.5">
                          <BoothActions
                            node={node}
                            link={link}
                            busy={busy}
                            onUnlink={onUnlink}
                            onOpenRelink={openRelink}
                            onOpenLink={openLink}
                          />
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Link / re-link popup — connect a booth to an org node */}
      <Modal
        open={Boolean(linkTarget)}
        onCancel={closeLink}
        footer={null}
        width={560}
        title={
          linkTarget
            ? `${linkTarget.mode === 'link' ? 'Link' : 'Relink'} “${linkTarget.label}”`
            : ''
        }
      >
        <p className="mb-3 text-sm text-ink-secondary">
          Select the organization node to {linkTarget?.mode === 'link' ? 'link' : 'move'} this unit
          to.
        </p>
        <input
          value={treeSearch}
          onChange={(e) => onTreeSearch(e.target.value)}
          placeholder="Search nodes…"
          className="mb-2 w-full rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink placeholder:text-ink-secondary focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
        />
        <div className="max-h-[340px] overflow-y-auto rounded-lg border border-line p-2">
          <SourceMapTree
            nodes={sourceTree}
            selectedNodeId={mapSel?.id ?? null}
            onSelect={(id, label) => setMapSel({ id, label })}
          />
        </div>
        {mapSel && (
          <p className="mt-3 text-sm text-ink-secondary">
            {linkTarget?.mode === 'link' ? 'Linking' : 'Moving'} to{' '}
            <span className="font-semibold text-ink">{mapSel.label}</span>
          </p>
        )}
        <div className="mt-4 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={closeLink}
            className="rounded-lg border border-line px-5 py-2 text-sm font-medium text-ink-secondary transition-colors hover:bg-brand-shade"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!mapSel || !linkTarget}
            onClick={() => {
              if (linkTarget && mapSel) {
                if (linkTarget.mode === 'relink') onRelink(linkTarget.nodeAreaId, mapSel.id);
                else onLink(linkTarget.areaCode, linkTarget.boothName, mapSel.id, linkTarget.kind);
              }
              closeLink();
            }}
            className="rounded-lg bg-brand px-6 py-2 text-sm font-medium text-white shadow-btn transition-colors hover:bg-brand-dark disabled:opacity-40"
          >
            {linkTarget?.mode === 'link' ? 'Link' : 'Relink'}
          </button>
        </div>
      </Modal>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Info cards
 * ------------------------------------------------------------------ */

function InfoCard({
  Icon,
  iconClass,
  badgeClass,
  title,
  subtitle,
  action,
}: {
  Icon: IconType;
  iconClass: string;
  badgeClass: string;
  title: string;
  subtitle: string;
  action: React.ReactNode;
}) {
  return (
    // Width-driven: single row when the card is wide; the action wraps below the text when it's
    // too narrow to fit alongside (e.g. the two-button Bulk Upload card in a tight column).
    <div className="flex flex-1 basis-[360px] flex-wrap items-center justify-between gap-x-6 gap-y-3 rounded-xl border border-line bg-white p-6 shadow-card">
      <div className="flex min-w-[180px] flex-1 items-center gap-4">
        <span
          className={['flex size-12 shrink-0 items-center justify-center rounded-full', badgeClass, iconClass].join(' ')}
        >
          <Icon className="h-5 w-4" />
        </span>
        <div className="flex min-w-0 flex-col">
          <span className="text-base font-semibold text-ink">{title}</span>
          <span className="text-[13px] text-ink-secondary">{subtitle}</span>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">{action}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Step
 * ------------------------------------------------------------------ */

const PILL = 'rounded bg-[#dce9ff] px-2 py-1 text-[11px] font-medium uppercase tracking-[0.55px] text-ink';

const FALLBACK_LEVELS = ['Alwar BJP', 'Mandal', 'Shakti Kendra', 'Booths'];

/** Step 4 — Organization Hierarchy Data Import. */
export default function DataImportStep({
  active = true,
  onConfirm,
  hierarchyLevels,
  selection,
  organizationId,
  sourceTitle = 'Internal Organization',
  bulkSubtitle = 'Import multiple organization levels',
  kind,
  staticSourceTree,
}: {
  /** This step is the one currently shown — gates its data fetches. */
  active?: boolean;
  onConfirm: () => void;
  hierarchyLevels?: string[];
  /** The real selected hierarchy from step 2 (state → … → booth). */
  selection?: HierarchyView[];
  organizationId?: number | null;
  /** SOURCE panel heading (e.g. "Administrative Hierarchy" for the admin-mapping step). */
  sourceTitle?: string;
  /** Bulk-upload card subtitle. */
  bulkSubtitle?: string;
  /** "admin" scopes the template/upload/node-tree calls to the administrative hierarchy. */
  kind?: string;
  /** Static SOURCE tree (demo/no-API pages) — bypasses the node-tree fetch when provided. */
  staticSourceTree?: OrgNode[];
}) {
  const dispatch = useAppDispatch();

  // Base tree = the step-2 path down to the chosen level (State → PC → AC). Deeper
  // levels (Rural/Urban → GP/Ward → Village → Booth) load lazily on drill, so we don't
  // fetch every village's booths up front.
  const destTree = useMemo(() => {
    const roots = (selection ?? []).flatMap((r) => (r.children.length > 0 ? r.children : [r]));
    return toBaseTree(roots);
  }, [selection]);

  // Lazily-loaded children keyed by node id, and the nodes currently fetching.
  const [childrenByNode, setChildrenByNode] = useState<Record<string, DestNode[]>>({});
  const [loadingNodes, setLoadingNodes] = useState<Set<string>>(new Set());
  // Reset the lazy cache when the selection changes.
  useEffect(() => {
    setChildrenByNode({});
    setLoadingNodes(new Set());
  }, [destTree]);

  const getChildren = useCallback(
    (node: DestNode): DestNode[] | undefined => childrenByNode[node.id] ?? node.children,
    [childrenByNode],
  );
  const loadChildren = useCallback(
    async (node: DestNode) => {
      if (
        node.located ||
        node.kind === 'booth' ||
        childrenByNode[node.id] ||
        (node.children?.length ?? 0) > 0 ||
        loadingNodes.has(node.id)
      ) {
        return;
      }
      setLoadingNodes((s) => new Set(s).add(node.id));
      try {
        const kids = await fetchDestChildren(dispatch, node);
        setChildrenByNode((prev) => ({ ...prev, [node.id]: kids }));
      } catch {
        /* ignore — that level just won't expand */
      } finally {
        setLoadingNodes((s) => {
          const n = new Set(s);
          n.delete(node.id);
          return n;
        });
      }
    },
    [dispatch, childrenByNode, loadingNodes],
  );

  // Auto-load the children of the level chosen in step 2 (e.g. Rural → its Gram
  // Panchayats) so the next level's list appears — only when this step is active.
  const selectedNode = useMemo(() => findSelectedNode(destTree), [destTree]);
  useEffect(() => {
    if (active && selectedNode && selectedNode.kind !== 'booth') loadChildren(selectedNode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode?.id, active]);

  // The org's internal hierarchy tree (left panel) — populated by the Excel upload.
  // Fetched only when this step is active (avoids calling it from step 1).
  const { data: fetchedNodeTree, isFetching: treeLoading } = useGetNodeTreeQuery(
    { organization: organizationId!, kind },
    { skip: !organizationId || !active || !!staticSourceTree },
  );
  // Static demo pages supply the SOURCE tree directly; API pages fetch it.
  const nodeTree = staticSourceTree ?? fetchedNodeTree;
  // Left-panel search — debounced into a server-side `?search=` node-tree query.
  const [panelSearch, setPanelSearch] = useState('');
  const [panelSearchQ, setPanelSearchQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setPanelSearchQ(panelSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [panelSearch]);
  const { data: panelTree, isFetching: panelLoading } = useGetNodeTreeQuery(
    { organization: organizationId!, kind, search: panelSearchQ || undefined },
    { skip: !organizationId || !active || !panelSearchQ },
  );
  // The tree shown in the left panel: the searched result when searching, else the full tree.
  const sourcePanelTree = panelSearchQ ? (panelTree ?? []) : (nodeTree ?? []);
  const sourcePanelLoading = panelSearchQ ? panelLoading : treeLoading;
  // Searchable copy of the tree for the link modal (debounced).
  const [treeSearch, setTreeSearch] = useState('');
  const [treeSearchQ, setTreeSearchQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setTreeSearchQ(treeSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [treeSearch]);
  const { data: linkTree } = useGetNodeTreeQuery(
    { organization: organizationId!, kind, search: treeSearchQ || undefined },
    { skip: !organizationId || !active },
  );

  // Booths already present in the Internal Organization tree, keyed by area_code — an
  // EC booth sharing a code is shown as already linked.
  const linkedAreas = useMemo(() => collectInternalAreas(nodeTree ?? []), [nodeTree]);

  // EcLocationLevel id → level_name, so a mapped area's real EC level (Booth / Village /
  // GramPanchayat / Ward …) picks the correct area-info `level` instead of guessing.
  const { data: ecLevels } = useGetEcLevelsQuery(undefined, { skip: !active });
  const ecLevelById = useMemo(() => {
    const m: Record<number, string> = {};
    for (const l of ecLevels ?? []) if (l.id != null && l.level_name) m[l.id] = l.level_name;
    return m;
  }, [ecLevels]);

  // node kind → ec_location_level_id, so linking any level (booth / village / GP / ward)
  // records the area at its true EC level (a village and a booth can share a numeric code).
  const ecLevelIdByKind = useMemo(() => {
    const norm = (s?: string | null) => (s || '').toLowerCase().replace(/[\s_-]+/g, '');
    const idByName: Record<string, number> = {};
    for (const l of ecLevels ?? []) if (l.id != null && l.level_name) idByName[norm(l.level_name)] = l.id;
    const m: Partial<Record<NodeKind, number>> = {};
    (Object.entries(KIND_TO_EC_LEVEL) as [NodeKind, string][]).forEach(([kind, name]) => {
      const id = idByName[name];
      if (id != null) m[kind] = id;
    });
    return m;
  }, [ecLevels]);

  // Locate an area (clicked in the Internal Organization panel) in the EC structure via
  // /territory/area-info/?level=…&code=… — shows where that booth/village/ward/assembly sits.
  const [located, setLocated] = useState<{
    code: string;
    name: string;
    loading: boolean;
    info?: AreaInfoDto;
  } | null>(null);
  const locateArea = useCallback(
    async (areaCode: string, areaName: string, levelHint?: string) => {
      setLocated({ code: areaCode, name: areaName, loading: true });
      try {
        // Prefer the node's declared level. If it's a standard EC level, normalize it;
        // otherwise pass the raw level_name through (e.g. admin levels like "dt"). Only
        // guess from the code when no level was provided at all.
        const level =
          normalizeLevel(levelHint) ??
          (levelHint && levelHint.trim() ? levelHint.trim() : levelFromAreaCode(areaCode));
        const res = await dispatch(
          territoryApi.endpoints.getAreaInfo.initiate(
            { level, code: areaCode },
            { subscribe: false },
          ),
        ).unwrap();
        setLocated({ code: areaCode, name: areaName, loading: false, info: res });
      } catch {
        setLocated({ code: areaCode, name: areaName, loading: false });
      }
    },
    [dispatch],
  );

  // Right-panel tree: the located area's EC structure when one is clicked, else the
  // step-2-derived tree.
  const ecTree = useMemo(
    () => (located?.info ? areaInfoToTree(located.info, located.code) : destTree),
    [located, destTree],
  );

  // Link (POST) / de-link (DELETE) / re-link (PATCH) a booth's node-area record. The org
  // tree refetches on success (OrgNode invalidation), keeping the linked state in sync.
  const [createNodeArea] = useCreateNodeAreaMutation();
  const [deleteNodeArea] = useDeleteNodeAreaMutation();
  const [updateNodeArea] = useUpdateNodeAreaMutation();
  // Booth whose link is currently changing — keyed by node-area id (linked) or area_code (linking).
  const [busyAreaId, setBusyAreaId] = useState<number | null>(null);
  const [busyCode, setBusyCode] = useState<string | null>(null);

  const handleUnlink = async (nodeAreaId: number) => {
    setBusyAreaId(nodeAreaId);
    try {
      await deleteNodeArea(nodeAreaId).unwrap();
    } catch (err) {
      setFeedback({ type: 'error', text: errText(err, 'Could not unlink the unit. Try again.') });
    } finally {
      setBusyAreaId(null);
    }
  };

  const handleRelink = async (nodeAreaId: number, organisationNodeId: number) => {
    setBusyAreaId(nodeAreaId);
    try {
      await updateNodeArea({ id: nodeAreaId, organisation_node: organisationNodeId }).unwrap();
    } catch (err) {
      setFeedback({ type: 'error', text: errText(err, 'Could not relink the unit. Try again.') });
    } finally {
      setBusyAreaId(null);
    }
  };

  const handleLink = async (
    areaCode: string,
    boothName: string,
    organisationNodeId: number,
    kind?: NodeKind,
  ) => {
    setBusyCode(areaCode);
    try {
      await createNodeArea({
        organisation_node: organisationNodeId,
        area_code: areaCode,
        booth_name: boothName,
        ec_location_level_id: kind ? ecLevelIdByKind[kind] : undefined,
      }).unwrap();
    } catch (err) {
      setFeedback({ type: 'error', text: errText(err, 'Could not link the unit. Try again.') });
    } finally {
      setBusyCode(null);
    }
  };

  const [triggerTemplate] = useLazyGetHierarchyTemplateQuery();
  const [triggerExport] = useLazyGetHierarchyExportQuery();
  const [uploadHierarchy, { isLoading: uploading }] = useUploadHierarchyMutation();
  const [downloading, setDownloading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-dismiss the feedback banner a few seconds after it appears.
  useEffect(() => {
    if (!feedback) return undefined;
    const t = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(t);
  }, [feedback]);

  // Prefer the backend's `detail` message when present.
  const errText = (err: unknown, fallback: string) => {
    const data = (err as { data?: unknown })?.data;
    if (data && typeof data === 'object' && 'detail' in data) {
      const d = (data as { detail?: unknown }).detail;
      if (typeof d === 'string') return d;
    }
    return fallback;
  };

  // Build a client-side .xlsx from the step-3 levels (fallback when there's no org id yet).
  const downloadLocalTemplate = () => {
    const levels = hierarchyLevels && hierarchyLevels.length > 0 ? hierarchyLevels : FALLBACK_LEVELS;
    const emptyRows = Array.from({ length: 20 }, () => levels.map(() => ''));
    const ws = XLSX.utils.aoa_to_sheet([levels, ...emptyRows]);
    ws['!cols'] = levels.map(() => ({ wch: 24 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Hierarchy');
    XLSX.writeFile(wb, 'organization-hierarchy-template.xlsx');
  };

  // Download the per-org template from the API; fall back to a local one if no org id.
  const downloadTemplate = async () => {
    setFeedback(null);
    if (!organizationId) {
      downloadLocalTemplate();
      return;
    }
    setDownloading(true);
    try {
      const blob = await triggerTemplate({ organization: organizationId, kind }).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'hierarchy.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setFeedback({
        type: 'error',
        text: errText(err, 'Could not download the template. Please try again.'),
      });
    } finally {
      setDownloading(false);
    }
  };

  // Download the org's already-uploaded hierarchy Excel so it can be edited & re-uploaded.
  const downloadUploaded = async () => {
    setFeedback(null);
    if (!organizationId) {
      setFeedback({ type: 'error', text: 'Please complete step 1 (create the organization) first.' });
      return;
    }
    setExporting(true);
    try {
      const res = await triggerExport({ organization: organizationId, kind }).unwrap();
      if (!(res instanceof Blob)) {
        setFeedback({ type: 'error', text: 'No uploaded hierarchy to download yet.' });
        return;
      }
      const url = URL.createObjectURL(res);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'hierarchy-export.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setFeedback({
        type: 'error',
        text: errText(err, 'Could not download the uploaded hierarchy. Please try again.'),
      });
    } finally {
      setExporting(false);
    }
  };

  const onFilePicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setFeedback(null);
    if (!organizationId) {
      setFeedback({ type: 'error', text: 'Please complete step 1 (create the organization) first.' });
      return;
    }
    try {
      await uploadHierarchy({ organization: organizationId, file, kind }).unwrap();
      setFeedback({ type: 'success', text: `“${file.name}” uploaded successfully.` });
    } catch (err) {
      setFeedback({
        type: 'error',
        text: errText(err, `Could not upload “${file.name}”. Check the file and try again.`),
      });
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Info cards — side by side when they fit, else stacked (two rows). */}
      <div className="flex flex-wrap gap-4">
        <InfoCard
          Icon={TemplateDocIcon}
          iconClass="text-brand"
          badgeClass="bg-[#cdd4ff]"
          title="Hierarchy Template"
          subtitle="Download the standardized structure"
          action={
            <button
              type="button"
              onClick={() => void downloadTemplate()}
              disabled={downloading}
              className="shrink-0 rounded-lg bg-brand px-4 py-2 text-xs font-semibold tracking-[0.24px] text-white transition-colors hover:bg-brand-dark disabled:opacity-60"
            >
              {downloading ? 'Downloading…' : 'Download XLSX'}
            </button>
          }
        />
        <InfoCard
          Icon={BulkUploadIcon}
          iconClass="text-[#943700]"
          badgeClass="bg-[#ffdbcd]"
          title="Bulk Upload Hierarchy"
          subtitle={bulkSubtitle}
          action={
            <div className="flex flex-wrap items-center justify-end gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={onFilePicked}
              />
              <button
                type="button"
                onClick={() => void downloadUploaded()}
                disabled={exporting}
                className="shrink-0 rounded-lg border border-brand px-4 py-2 text-xs font-semibold tracking-[0.24px] text-brand transition-colors hover:bg-brand-shade disabled:opacity-60"
              >
                {exporting ? 'Downloading…' : 'Download Uploaded'}
              </button>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="shrink-0 rounded-lg bg-brand px-4 py-2 text-xs font-semibold tracking-[0.24px] text-white transition-colors hover:bg-brand-dark disabled:opacity-60"
              >
                {uploading ? 'Uploading…' : 'Select Files'}
              </button>
            </div>
          }
        />
      </div>

      {feedback && (
        <p
          className={[
            'rounded-lg border px-4 py-3 text-sm font-medium',
            feedback.type === 'success'
              ? 'border-green-200 bg-green-50 text-green-700'
              : 'border-red-200 bg-red-50 text-[#ba1a1a]',
          ].join(' ')}
        >
          {feedback.text}
        </p>
      )}

      {/* Explorer */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-stretch">
        {/* SOURCE */}
        <div className="flex h-[640px] flex-col overflow-hidden rounded-xl bg-white shadow-card lg:w-[400px]">
          <div className="flex items-center justify-between border-b border-line bg-[rgba(229,238,255,0.5)] px-4 py-4">
            <div className="flex items-center gap-3">
              <span className={PILL}>Source</span>
              <h2 className="text-base font-semibold text-ink">{sourceTitle}</h2>
            </div>
            <button type="button" aria-label="Filter" className="text-ink">
              <FilterIcon className="h-[10px] w-[15px]" />
            </button>
          </div>

          <div className="border-b border-line px-4 py-4">
            <div className="relative">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-[13.5px] -translate-y-1/2 text-ink-secondary/70" />
              <input
                type="search"
                value={panelSearch}
                onChange={(e) => setPanelSearch(e.target.value)}
                placeholder="Search units..."
                className="w-full rounded-lg bg-[#e5eeff] py-2.5 pl-10 pr-4 text-[13px] text-ink placeholder:text-ink-secondary/70 focus:outline-none focus:ring-2 focus:ring-brand/15"
              />
            </div>
          </div>

          <div className="flex-1 overflow-auto p-4">
            {sourcePanelLoading ? (
              <div className="flex h-full items-center justify-center">
                <Spinner />
              </div>
            ) : sourcePanelTree.length > 0 ? (
              <InternalOrgTree
                nodes={sourcePanelTree}
                onBoothClick={locateArea}
                activeCode={located?.code}
                ecLevelById={ecLevelById}
                leafLocate={kind === 'admin'}
              />
            ) : panelSearchQ ? (
              <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
                <p className="text-sm text-ink-secondary">No units match “{panelSearchQ}”.</p>
              </div>
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
                <BulkUploadIcon className="size-6 text-ink-secondary/50" />
                <p className="text-sm text-ink-secondary">
                  Upload the hierarchy Excel to populate the internal organization structure.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* DESTINATION */}
        <div className="flex h-[640px] flex-col overflow-hidden rounded-xl bg-white shadow-card lg:flex-1">
          <div className="flex items-center justify-between border-b border-line bg-[rgba(229,238,255,0.5)] px-4 py-4">
            <div className="flex items-center gap-3">
              <span className={PILL}>Destination</span>
              <h2 className="text-base font-semibold text-ink">Election Commission Structure</h2>
              {loadingNodes.size > 0 && (
                <span className="flex items-center gap-1.5 text-xs text-ink-secondary">
                  <Spinner size={14} /> Loading…
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-ink">
              <button
                type="button"
                aria-label="Columns view"
                className="flex size-8 items-center justify-center rounded-lg hover:bg-brand-shade"
              >
                <ColumnsIcon className="h-[11px] w-[15px]" />
              </button>
              <button
                type="button"
                aria-label="More options"
                className="flex size-8 items-center justify-center rounded-lg hover:bg-brand-shade"
              >
                <KebabIcon className="h-[13px] w-[3px]" />
              </button>
            </div>
          </div>

          {/* Located booth (clicked in the Internal Organization panel) */}
          {located && (
            <div className="flex items-start justify-between gap-3 border-b border-brand/20 border-l-4 border-l-brand bg-gradient-to-r from-[#dce9ff] to-[#eff4ff] px-4 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]">
              <div className="flex items-start gap-2.5">
                <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-md bg-brand/10 text-brand">
                  <HierarchyNodesIcon className="size-3.5" />
                </span>
                <div className="flex flex-col gap-0.5">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.5px] text-brand-dark">
                    {located.info?.level || 'Area'} location · {located.code}
                  </span>
                  {located.loading ? (
                    <span className="flex items-center gap-1.5 text-xs text-ink-secondary">
                      <Spinner size={12} /> Locating “{located.name}”…
                    </span>
                  ) : located.info ? (
                    <span className="text-sm font-semibold text-brand-dark">
                      {areaLocationPath(located.info) || located.name}
                      {located.info.name_hi ? (
                        <span className="ml-1.5 font-normal text-ink-secondary">
                          ({located.info.name_hi})
                        </span>
                      ) : null}
                    </span>
                  ) : (
                    <span className="text-xs text-ink-secondary">
                      No location found for this area.
                    </span>
                  )}
                  {/* Village attributes from area-info (type / urban-rural / electors). */}
                  {located.info &&
                    (() => {
                      const i = located.info;
                      const chips = [
                        i.type,
                        i.urban_rural,
                        i.total_electors != null
                          ? `${i.total_electors.toLocaleString('en-IN')} electors`
                          : undefined,
                      ].filter(Boolean) as string[];
                      return chips.length ? (
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {chips.map((c) => (
                            <span
                              key={c}
                              className="rounded-full bg-white/70 px-2 py-0.5 text-[11px] font-medium text-brand-dark"
                            >
                              {c}
                            </span>
                          ))}
                        </div>
                      ) : null;
                    })()}
                </div>
              </div>
              <button
                type="button"
                aria-label="Clear"
                onClick={() => setLocated(null)}
                className="shrink-0 rounded px-1 text-lg leading-none text-ink-secondary transition-colors hover:text-ink"
              >
                ×
              </button>
            </div>
          )}

          {/* Miller-columns view (breadcrumb + LEVEL N columns) */}
          {ecTree.length > 0 ? (
            <DestinationColumns
              tree={ecTree}
              getChildren={getChildren}
              onDrill={loadChildren}
              loadingNodes={loadingNodes}
              linkedAreas={linkedAreas}
              sourceTree={linkTree ?? nodeTree ?? []}
              treeSearch={treeSearch}
              onTreeSearch={setTreeSearch}
              onLink={handleLink}
              onUnlink={handleUnlink}
              onRelink={handleRelink}
              busyAreaId={busyAreaId}
              busyCode={busyCode}
            />
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 bg-[#f8f9ff] px-6 text-center">
              <HierarchyNodesIcon className="size-6 text-ink-secondary/50" />
              <p className="text-sm text-ink-secondary">
                Select constituency boundaries in Step 2 to populate the Election Commission
                structure.
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end border-t border-line bg-white px-4 py-4">
            <button
              type="button"
              onClick={onConfirm}
              className="flex items-center gap-2 rounded-lg bg-brand px-6 py-2.5 text-xs font-semibold tracking-[0.24px] text-white shadow-btn transition-colors hover:bg-brand-dark"
            >
              Confirm Selection
              <ArrowRightIcon className="h-[9px] w-[16px]" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
